/**
 * Mock Authentication Provider for Local Development
 *
 * Provides a fake authentication context that simulates Cognito login
 * without making any external API calls. This enables offline development
 * and testing without requiring real Cognito credentials.
 *
 * This component creates a mock authentication experience using react-oidc-context
 * by intercepting and overriding the UserManager methods.
 *
 * Usage:
 *   <MockAuthProvider mockEmail="dev@example.com" mockName="Developer">
 *     <App />
 *   </MockAuthProvider>
 */

import React, { useMemo } from 'react';
import { AuthProvider as ReactOidcAuthProvider } from 'react-oidc-context';
import { WebStorageStateStore, UserManager, Log } from 'oidc-client-ts';

interface MockAuthProviderProps {
  children: React.ReactNode;
  /** Mock user email (from VITE_MOCK_USER_EMAIL) */
  mockEmail?: string;
  /** Mock user name (from VITE_MOCK_USER_NAME) */
  mockName?: string;
}

/**
 * Mock authentication provider component
 *
 * Creates a custom UserManager that provides mock authentication
 * without making any network calls.
 */
export function MockAuthProvider({
  children,
  mockEmail = 'dev@example.com',
  mockName = 'Local Developer'
}: MockAuthProviderProps): JSX.Element {
  // Suppress oidc-client-ts warnings in mock mode
  Log.setLogger(console);
  Log.setLevel(Log.WARN);

  // Create a custom UserManager with overridden methods
  const userManager = useMemo(() => {
    const config = {
      authority: 'http://mock-authority',
      client_id: 'mock-client-id',
      redirect_uri: window.location.origin + '/',
      post_logout_redirect_uri: window.location.origin + '/',
      response_type: 'code',
      scope: 'openid email profile',
      automaticSilentRenew: false,
      loadUserInfo: false,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      // Prevent metadata endpoint calls
      metadata: {
        issuer: 'http://mock-authority',
        authorization_endpoint: 'http://mock-authority/authorize',
        token_endpoint: 'http://mock-authority/token',
        userinfo_endpoint: 'http://mock-authority/userinfo',
        end_session_endpoint: 'http://mock-authority/logout',
        jwks_uri: 'http://mock-authority/.well-known/jwks.json'
      }
    };

    const manager = new UserManager(config);

    // Store the original methods
    const originalSigninRedirect = manager.signinRedirect.bind(manager);
    const originalSignoutRedirect = manager.signoutRedirect.bind(manager);
    const originalSigninSilent = manager.signinSilent.bind(manager);

    // Override signinRedirect to create mock user without network call
    manager.signinRedirect = async () => {
      const now = Math.floor(Date.now() / 1000);

      // Create mock user data in the format oidc-client-ts expects
      const mockUserData = {
        id_token: 'mock-id-token',
        session_state: null,
        access_token: 'mock-access-token',
        refresh_token: 'mock-refresh-token',
        token_type: 'Bearer',
        scope: 'openid email profile',
        profile: {
          sub: 'mock-user-id',
          email: mockEmail,
          name: mockName,
          preferred_username: mockEmail,
          'cognito:username': mockEmail,
          iss: 'http://mock-authority',
          aud: 'mock-client-id',
          exp: now + 3600,
          iat: now,
          auth_time: now,
        },
        expires_at: now + 3600,
      };

      // Store directly in sessionStorage in the format oidc-client-ts expects
      const key = `oidc.user:${config.authority}:${config.client_id}`;
      window.sessionStorage.setItem(key, JSON.stringify(mockUserData));

      // Trigger the user loaded event to update react-oidc-context state
      // This forces the context to recognize the new user without a page refresh
      const user = await manager.getUser();
      if (user) {
        // Use the public events API instead of private _userLoaded
        // @ts-ignore - accessing internal API for mock purposes
        manager.events._userLoaded?.raise(user);
      }

      return Promise.resolve();
    };

    // Override signoutRedirect to clear mock user
    manager.signoutRedirect = async () => {
      await manager.removeUser();
      return Promise.resolve();
    };

    // Override signinSilent to return current user
    manager.signinSilent = async () => {
      return await manager.getUser();
    };

    return manager;
  }, [mockEmail, mockName]);

  return (
    <ReactOidcAuthProvider
      userManager={userManager}
      onSigninCallback={() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }}
    >
      {children}
    </ReactOidcAuthProvider>
  );
}
